/**
 * Distinguishes a genuine deep-link chapter hash (`#<chapterTitle>` — someone followed or typed it)
 * from the hash `ViewOnDemandWebinarPage` writes itself via `history.replaceState` as a shareable
 * "current position" URL, on every active-chapter change. Both look identical in
 * `window.location.hash`, so on a reload there is no way to tell them apart from the hash alone —
 * and since a URL hash deliberately always wins over resume (a real deep-link should), an
 * auto-written hash would otherwise permanently suppress resume for any chaptered on-demand: you'd
 * always land back at that chapter's start instead of your last position.
 *
 * sessionStorage is the right signal: it survives a same-tab reload (so our own auto-write is still
 * recognised after a refresh) but starts empty for a fresh tab/window and is never shared across
 * browsers — exactly the cases where the hash IS a genuine external deep-link.
 */

const key = (webinarId: string) => `onDemandAutoHash:${webinarId}`;

/** Call every time the hash is rewritten to the active chapter, with that same chapter's title. */
export function markAutoHash(webinarId: string, chapterTitle: string): void {
    try {
        globalThis.sessionStorage?.setItem(key(webinarId), chapterTitle);
    } catch {
        /* ignore */
    }
}

/** Call when the auto-written hash is removed again, so the mark never outlives the hash it describes. */
export function clearAutoHash(webinarId: string): void {
    try {
        globalThis.sessionStorage?.removeItem(key(webinarId));
    } catch {
        /* ignore */
    }
}

/** True when `chapterTitle` is exactly the hash this tab last auto-wrote for this webinar. */
export function isAutoHash(webinarId: string, chapterTitle: string): boolean {
    try {
        return globalThis.sessionStorage?.getItem(key(webinarId)) === chapterTitle;
    } catch {
        return false;
    }
}
